import React, { useState, useEffect } from 'react';

// Components and others
import clsx from 'clsx';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import If from 'components/widgets/conditional/If';
import Container from 'components/widgets/Container';
import { modules } from 'utils/enum/modules';

import LeadWidget from 'components/modules/messageCenter/read/LeadWidget';
import DealWidget from 'components/modules/messageCenter/read/DealWidget';
import OpportunityWidget from 'components/modules/messageCenter/read/OpportunityWidget';
import MessageCenterList from 'components/modules/messageCenter/list/MessageCenterList';
import MessageCenterCustomerContext from 'components/context/MessageCenterCustomerContext';
import MessageCenterDetail from 'components/modules/messageCenter/read/MessageCenterDetail';
import ConversationPlaceholder from 'components/modules/conversation/list/ConversationPlaceholder';

// Material UI
import {
    Typography, Tooltip, useMediaQuery,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Utils
import KeyStore from 'utils/KeyStore';
import Permission from 'utils/enum/Permissions';
import { MEDIA_QUERY } from 'utils/enum/Core';

const keyStore = new KeyStore();
const useStyles = makeStyles((theme) => ({
    main: {
        flex: 1,
        display: 'flex',
        overflow: 'hidden',
        flexDirection: 'column',
    },
    title: {
        color: theme.palette.text.boulderGray,
    },
    detail: {
        display: 'flex',
        flexGrow: 1,
        overflow: 'auto',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            backgroundColor: theme.palette.background.white,
        },
    },
    detailHidden: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    listContainer: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            width: '100%',
            overflow: 'hidden',
        },
    },
    listHidden: {
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    customer: {
        color: theme.palette.text.butterflyBush,
        marginBottom: theme.spacing(0.5),
    },
    customerInformation: {
        padding: theme.spacing(2, 1),
        backgroundColor: theme.palette.background.white,
        marginBottom: theme.spacing(2),
        display: 'flex',
        '& > div:nth-child(1)': {
            width: '80%',
        },
        '& > div:nth-child(2)': {
            width: '20%',
            fontSize: '18px',
            display: 'flex',
            justifyContent: 'flex-end',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            '& > div:nth-child(1)': {
                width: '100%',
            },
            '& > div:nth-child(2)': {
                display: 'none',
            },
        },
    },
    opportunityContainer: {
        padding: theme.spacing(1),
        height: '100%',
        overflow: 'auto',
        width: '690px',
        minWidth: '690px',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    column: {
        position: 'relative',
        height: '100%',
    },
    leftSection: {
        borderLeft: '1px solid #eeeeee',
        background: '#F7FAFF',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            borderLeft: 'none',
        },
    },
    label: {
        color: theme.palette.text.minsk,
        fontSize: '14px',
        fontWeight: '500',
        textAlign: 'right',
        marginRight: theme.spacing(1),
        lineHeight: '20px',
    },
    value: {
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '20px',
        color: theme.palette.text.gray,
    },
}));

const MessageCenter = () => {
    const classes = useStyles();
    const history = useHistory();
    const { id } = useParams();
    const isMobile = useMediaQuery(`(max-width:${MEDIA_QUERY.W_430}px)`);
    const [customer, setCustomer] = useState({});
    const [showDetail, setShowDetail] = useState(false);

    const location = useLocation();
    const query = new URLSearchParams(location.search || '');
    const smsRead = query.get('smsRead');

    let name = 'Unknown';
    let email = null;
    if (customer?.customerId != null) {
        name = `${customer.firstName || ''} ${customer.lastName || ''}`;
        email = customer.email;
    }

    useEffect(() => {
        if (id && isMobile) {
            setShowDetail(true);
        } else if (!id && isMobile) {
            setShowDetail(false);
        }
    }, [id, isMobile]);

    const handleBackToList = () => {
        setShowDetail(false);
        history.push(`/${modules.COMMUNICATION}/sms`);
    };

    const renderConversationDetails = () => {
        if (id && customer?.customerId) {
            return (
                <div className={clsx(classes.detail, isMobile && !showDetail && classes.detailHidden)}>
                    <MessageCenterDetail
                        smsChatId={id}
                        customer={customer}
                        smsRead={smsRead}
                        onBackToList={isMobile ? handleBackToList : null}
                    />
                    <div className={clsx(classes.opportunityContainer, classes.leftSection)}>
                        <div className={classes.customerInformation}>
                            <div>
                                <Typography variant="h4" className={classes.customer} title={name} noWrap>{name}</Typography>
                                <If condition={email !== null}>
                                    <div>
                                        <span className={classes.label}>Email: </span>
                                        <span className={classes.value}>{email}</span>
                                    </div>
                                </If>
                            </div>
                            <Tooltip title="Open Opportunity" placement="top">
                                <div>
                                    <a href={`/${modules.OPPORTUNITIES}/${customer.opportunity.crmId}`} target="_blank" rel="noopener noreferrer">
                                        {customer.opportunity.leadCode}
                                    </a>
                                </div>
                            </Tooltip>
                        </div>
                        <LeadWidget
                            customerId={customer.customerId}
                        />
                        <OpportunityWidget
                            customerId={customer.customerId}
                        />
                        <DealWidget
                            customerId={customer.customerId}
                        />
                    </div>
                </div>
            );
        }

        return (
            !isMobile && <ConversationPlaceholder />
        );
    };

    return (
        <div className={classes.main}>
            <MessageCenterCustomerContext.Provider value={{ customer, setCustomer }}>
                <Container padding={false} backgroundColor={false}>
                    {keyStore.hasPermission(Permission.CRM_MESSAGE_CENTER_READ) && (
                        <div className={clsx(classes.listContainer, isMobile && showDetail && classes.listHidden)}>
                            <MessageCenterList conversationId={id} />
                        </div>
                    )}
                    {keyStore.hasPermission(Permission.CRM_MESSAGE_CENTER_READ) && renderConversationDetails()}
                </Container>
            </MessageCenterCustomerContext.Provider>
        </div>
    );
};

export default MessageCenter;
